import React, { useEffect, useRef, useState } from "react";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import { MessageContent } from "components/atoms/message";
import {
    formatReviewThreadCreatedAt,
    formatReviewThreadLocation,
    getReviewThreadExtraMessageCount,
    getReviewThreadFirstMessage,
    getReviewThreadMessageCountLabel,
    getReviewThreadSelectedText,
    getReviewThreadStatusLabel,
} from "components/organisms/manuscript/review/formatters";

const PanelHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 12px 14px;
    border-bottom: 1px solid #dde6f0;
    background: #f6f9fc;
`;

const HeaderInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
`;

const HeaderText = styled.div`
    min-width: 0;
`;

const Title = styled.div`
    font-size: 13px;
    font-weight: 800;
    color: #132238;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Subtitle = styled.div`
    font-size: 11px;
    color: #6d7f96;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const RailBody = styled.div`
    flex: 1;
    min-height: 0;
    padding: 10px;
    overflow: auto;
    background: #fbfcfe;
`;

const Stack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const MetaValue = styled.div`
    min-width: 0;
    color: #536981;
    word-break: break-word;
`;

const ReviewList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const ReviewToolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    flex-wrap: wrap;
`;

const ReviewFilterGroup = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
`;

const CompactTextButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 24px;
    padding: 0 8px;
    border: 1px solid #d5dee8;
    border-radius: 6px;
    background: #ffffff;
    color: #32485e;
    font-size: 10px;
    font-weight: 700;
    cursor: pointer;

    &:hover {
        background: #eef3f7;
    }
`;

const ReviewThreadItem = styled.button`
    display: flex;
    flex-direction: column;
    gap: 5px;
    width: 100%;
    padding: 8px 10px;
    border: 1px solid ${(props) => (props.$active ? "#b8d8cc" : "#dde7f1")};
    border-radius: 9px;
    background: ${(props) => (props.$active ? "#eef9f4" : "#ffffff")};
    text-align: left;
    cursor: pointer;

    &:hover {
        background: ${(props) => (props.$active ? "#eef9f4" : "#f7fafc")};
    }
`;

const ReviewThreadTop = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const ReviewThreadTitle = styled.div`
    font-size: 11px;
    font-weight: 700;
    color: #1d3247;
`;

const ReviewThreadTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    flex-wrap: wrap;
`;

const ReviewThreadCount = styled.span`
    font-size: 10px;
    font-weight: 700;
    color: #6d8197;
`;

const ReviewBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    height: 18px;
    padding: 0 7px;
    border-radius: 999px;
    background: ${(props) => (props.$resolved ? "#e5ecf4" : "#e7faf3")};
    color: ${(props) => (props.$resolved ? "#5f7489" : "#0f8f6f")};
    font-size: 9px;
    font-weight: 800;
`;

const ReviewThreadLine = styled.div`
    font-size: 10px;
    color: #698096;
`;

const ReviewThreadSelectedText = styled.div`
    font-size: 11px;
    color: #465d74;
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
`;

const ReviewThreadMessageCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 9px 10px;
    border: 1px solid #dde7f1;
    border-radius: 10px;
    background: #fbfdff;
`;

const ReviewThreadMessageMeta = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 10px;
    color: #6d8197;
`;

const ReviewThreadPreview = styled.div`
    font-size: 11px;
    line-height: 1.4;
    color: #344b60;
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
`;

const ReviewThreadExtraMessages = styled.div`
    font-size: 10px;
    color: #6d8197;
`;

function ManuscriptReviewPanel({ reviewThreads, selectedReviewThreadId, onSelectReviewThread, onReviewThreadMount }) {
    const [reviewFilter, setReviewFilter] = useState("open");
    const selectedReviewThreadRef = useRef(null);
    const getAuthorLabel = (name, email) => name || email || "알 수 없음";
    const visibleReviewThreads = reviewThreads.filter((thread) => {
        if (reviewFilter === "all") return true;
        if (reviewFilter === "resolved") return thread.is_resolved;
        return !thread.is_resolved;
    });
    const openReviewCount = reviewThreads.filter((thread) => !thread.is_resolved).length;
    const resolvedReviewCount = reviewThreads.filter((thread) => thread.is_resolved).length;
    const totalReviewCount = reviewThreads.length;

    useEffect(() => {
        if (!selectedReviewThreadRef.current) return;
        selectedReviewThreadRef.current.scrollIntoView({
            block: "nearest",
            behavior: "smooth",
        });
    }, [selectedReviewThreadId, reviewFilter]);

    return (
        <>
            <PanelHeader>
                <HeaderInfo>
                    <Icon name={"thread"} size={"16px"} color={"#0f8f6f"} />
                    <HeaderText>
                        <Title>리뷰</Title>
                        <Subtitle>선택 영역에 코멘트를 남깁니다.</Subtitle>
                    </HeaderText>
                </HeaderInfo>
            </PanelHeader>
            <RailBody>
                <Stack>
                    <ReviewToolbar>
                        <ReviewFilterGroup>
                            <CompactTextButton
                                type="button"
                                onClick={() => setReviewFilter("open")}
                                style={
                                    reviewFilter === "open" ? { borderColor: "#0f8f6f", color: "#0f8f6f" } : undefined
                                }
                            >
                                열림 ({openReviewCount})
                            </CompactTextButton>
                            <CompactTextButton
                                type="button"
                                onClick={() => setReviewFilter("resolved")}
                                style={
                                    reviewFilter === "resolved"
                                        ? { borderColor: "#0f8f6f", color: "#0f8f6f" }
                                        : undefined
                                }
                            >
                                해결됨 ({resolvedReviewCount})
                            </CompactTextButton>
                            <CompactTextButton
                                type="button"
                                onClick={() => setReviewFilter("all")}
                                style={
                                    reviewFilter === "all" ? { borderColor: "#0f8f6f", color: "#0f8f6f" } : undefined
                                }
                            >
                                전체 ({totalReviewCount})
                            </CompactTextButton>
                        </ReviewFilterGroup>
                    </ReviewToolbar>

                    <ReviewList>
                        {visibleReviewThreads.length ? (
                            visibleReviewThreads.map((thread) => {
                                const firstMessage = getReviewThreadFirstMessage(thread);
                                const location = formatReviewThreadLocation(thread.creation_anchor_range);
                                const selectedText = getReviewThreadSelectedText(thread);
                                const extraMessageCount = getReviewThreadExtraMessageCount(thread);
                                return (
                                    <ReviewThreadItem
                                        key={thread.id}
                                        type="button"
                                        data-testid={`review-thread-item-${thread.id}`}
                                        ref={(element) => {
                                            if (thread.id === selectedReviewThreadId) {
                                                selectedReviewThreadRef.current = element;
                                            }
                                            onReviewThreadMount?.(thread.id, element);
                                        }}
                                        $active={thread.id === selectedReviewThreadId}
                                        onClick={() => onSelectReviewThread?.(thread.id, { source: "panel" })}
                                    >
                                        <ReviewThreadTop>
                                            <ReviewThreadTitleRow>
                                                <ReviewThreadTitle>{thread.anchor_path}</ReviewThreadTitle>
                                                <ReviewThreadCount>
                                                    {getReviewThreadMessageCountLabel(thread)}
                                                </ReviewThreadCount>
                                            </ReviewThreadTitleRow>
                                            <ReviewBadge $resolved={thread.is_resolved}>
                                                {getReviewThreadStatusLabel(thread)}
                                            </ReviewBadge>
                                        </ReviewThreadTop>
                                        {location ? <ReviewThreadLine>{location}</ReviewThreadLine> : null}
                                        {selectedText ? (
                                            <ReviewThreadSelectedText>{selectedText}</ReviewThreadSelectedText>
                                        ) : null}
                                        <ReviewThreadMessageCard>
                                            <ReviewThreadMessageMeta>
                                                <span>
                                                    {getAuthorLabel(
                                                        firstMessage?.sender_name || thread.created_by_name,
                                                        firstMessage?.sender_email || thread.created_by_email,
                                                    )}
                                                </span>
                                                <span>
                                                    {formatReviewThreadCreatedAt(
                                                        firstMessage?.created_at || thread.created_at,
                                                    )}
                                                </span>
                                            </ReviewThreadMessageMeta>
                                            <ReviewThreadPreview>
                                                {firstMessage?.content ? (
                                                    <MessageContent content={firstMessage.content} />
                                                ) : (
                                                    "첫 메시지가 없습니다."
                                                )}
                                            </ReviewThreadPreview>
                                        </ReviewThreadMessageCard>
                                        {extraMessageCount ? (
                                            <ReviewThreadExtraMessages>
                                                추가 메시지 {extraMessageCount}개
                                            </ReviewThreadExtraMessages>
                                        ) : null}
                                    </ReviewThreadItem>
                                );
                            })
                        ) : (
                            <MetaValue>표시할 리뷰가 없습니다.</MetaValue>
                        )}
                    </ReviewList>
                </Stack>
            </RailBody>
        </>
    );
}

export default ManuscriptReviewPanel;
